/**
 * Decide, POR RANGOS, si un cierre de conexión de Pusher merece un issue en
 * Sentry o es ruido esperable.
 *
 * ## Por qué hace falta
 *
 * `DASHBOARD-ZC-KK` y `-KV` llevan meses acumulando eventos con
 * `code: 'unknown'`. El PR #232 arregla la LECTURA del código — sin eso, esto
 * no se podría escribir, porque no habría por qué clasificar. Con el código
 * real a la vista aparece lo siguiente: buena parte de esos eventos son
 * **caídas de red del propio cliente** (el asesor cerró la laptop, se le fue el
 * wifi, el navegador mató el socket). No hay nada que arreglar ahí, y ese ruido
 * entierra los cierres que sí son problema nuestro o del proveedor.
 *
 * ## La tabla está anclada al fuente del vendor, no a la memoria
 *
 * `pusher-js@7.6.0`, `protocol.getCloseAction()` — el propio SDK clasifica los
 * códigos en tramos y decide qué hacer con cada uno:
 *
 * | Código      | Acción de pusher-js | Qué significa                     |
 * |-------------|---------------------|-----------------------------------|
 * | `< 4000`    | `null`              | no reconecta (cierre del cliente) |
 * | `1002–1004` | `'backoff'`         | error de protocolo                |
 * | `4000`      | `'tls_only'`        | hay que usar TLS                  |
 * | `4001–4099` | `'refused'`         | rechazado, terminal               |
 * | `4100–4199` | `'backoff'`         | sobre capacidad                   |
 * | `4200–4299` | `'retry'`           | transitorio, reconecta ya         |
 * | `>= 4300`   | `'refused'`         | rechazado, sin clasificar         |
 *
 * `getCloseError()` además NO construye error para 1000 ni 1001, así que esos
 * dos ni siquiera llegan al handler. Se dejan en la tabla igual, como defensa.
 *
 * ## Las dos decisiones de la zona ambigua, con su porqué
 *
 * **`1002–1004` (error de protocolo) → SE REPORTA.** El propio SDK los separa
 * del resto de los `< 4000`: a la caída de red le da `null` y a éstos
 * `'backoff'`. O sea, el vendor mismo dice que no son lo mismo. Un error de
 * protocolo significa que alguien está estropeando los frames — versión del
 * SDK, un proxy corporativo, o el proveedor — y no se cura solo. Es raro y es
 * accionable: justo el perfil de lo que sí queremos ver.
 *
 * **`4200–4299` (transitorio) → SE IGNORA.** La acción del vendor es `'retry'`:
 * Pusher le está diciendo al cliente "vuelve ya", y se recupera en menos de un
 * segundo. Reportarlo sería un issue por cada parpadeo de red del proveedor.
 *
 * Lo que hace segura esta decisión — y no un punto ciego — es que **no nos deja
 * a oscuras si deja de ser transitorio**: `pusherInstrumentation.js` captura
 * aparte los estados `unavailable` y `failed` de la conexión
 * (`pusher-connection-unavailable`). Si un 42xx dejara de recuperarse, la
 * conexión se queda en `unavailable` y eso SÍ se reporta. Cambiamos un evento
 * por parpadeo por una señal que mide lo que de verdad importa: que el asesor
 * se quedó sin tiempo real.
 *
 * ## La regla dura
 *
 * **Si el código no se puede clasificar, SE REPORTA.** Vale para `'unknown'`,
 * ausente, no numérico, y para cualquier código que no caiga en un tramo
 * conocido. Este filtro existe para quitar ruido identificado, no para volverse
 * la vía por la que se traga lo que no entendemos: eso sería tapar el problema
 * en vez de arreglarlo, y es exactamente el agujero del que venimos.
 */

/** Se devuelve cuando el código no es un número utilizable. */
const UNPARSEABLE = null;

/**
 * Normaliza el `code` que entrega `normalizePusherError`, que puede venir como
 * número, como string numérico, o como el literal `'unknown'`.
 *
 * Sólo se aceptan ENTEROS. Un código de cierre de WebSocket es entero por
 * definición (RFC 6455 §7.4), así que un decimal no es un código: es algo que
 * no sabemos leer. Y como los tramos son rangos cerrados, un decimal se colaría
 * DENTRO de uno — `1000.5` caería en `1000–1001` y se ignoraría en silencio.
 * Rechazarlo aquí lo manda a la regla dura (`unparseable-code` → se reporta),
 * que es el lado seguro: nunca tragarse lo que no se entiende.
 *
 * `Number.isInteger` ya descarta `NaN` e `Infinity`, así que subsume al
 * `Number.isFinite` que había antes.
 *
 * @returns {number|null} el código, o `null` si no hay entero que clasificar.
 */
export function toCloseCode(code) {
  if (typeof code === 'number') {
    return Number.isInteger(code) ? code : UNPARSEABLE;
  }

  if (typeof code === 'string') {
    const trimmed = code.trim();
    // `Number('')` es 0 — un string vacío NO es el código de cierre 0.
    if (trimmed === '') return UNPARSEABLE;
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) ? parsed : UNPARSEABLE;
  }

  return UNPARSEABLE;
}

/**
 * Tabla de tramos, en orden. El primero que contiene al código gana.
 *
 * Es una tabla y no una cadena de `if` a propósito: los límites quedan a la
 * vista y se comparan de un vistazo con la tabla del vendor de arriba. Añadir
 * un tramo es añadir una fila.
 *
 * `reason` es un slug corto y estable: va a un tag de Sentry, así que no lleva
 * texto del proveedor ni datos de nadie.
 */
export const CLOSE_CODE_RANGES = [
  // Cierres normales del cliente. pusher-js ni siquiera construye un error para
  // ellos (`getCloseError` los excluye), pero se declaran por completitud.
  { from: 1000, to: 1001, report: false, reason: 'client-normal-close' },

  // Error de protocolo. El vendor les da 'backoff', no `null`: los distingue de
  // la caída de red. Ver la justificación de arriba.
  { from: 1002, to: 1004, report: true, reason: 'protocol-error' },

  // 1005 = sin código de cierre; 1006 = cierre anormal (se cayó la red del
  // cliente). pusher-js les da `null`: no reconecta y no hay nada que arreglar.
  { from: 1005, to: 1006, report: false, reason: 'client-network-drop' },

  // 4000 tls_only, 4004 sobre cuota de conexiones, 4009 no autorizado…
  // Configuración, cuota o autenticación: problema nuestro, y terminal.
  { from: 4000, to: 4099, report: true, reason: 'refused-config-or-quota' },

  // Sobre capacidad del proveedor. No es culpa nuestra, pero sí accionable:
  // es la señal para reclamar o subir el plan.
  { from: 4100, to: 4199, report: true, reason: 'over-capacity' },

  // Transitorio: el vendor reconecta de inmediato. Ver la justificación.
  { from: 4200, to: 4299, report: false, reason: 'transient-retry' },

  // Rechazado sin clasificar. Es el tramo que el proveedor usa para lo que
  // todavía no tiene nombre — precisamente por eso se reporta.
  { from: 4300, to: Infinity, report: true, reason: 'unclassified-refused' },
];

/**
 * Clasifica un cierre de Pusher.
 *
 * @param {number|string|undefined|null} code - el `code` de `normalizePusherError`.
 * @returns {{ report: boolean, reason: string }}
 */
export function classifyPusherClose(code) {
  const parsed = toCloseCode(code);

  // REGLA DURA: sin número que clasificar, se reporta.
  if (parsed === UNPARSEABLE) {
    return { report: true, reason: 'unparseable-code' };
  }

  const range = CLOSE_CODE_RANGES.find(
    (r) => parsed >= r.from && parsed <= r.to,
  );

  // Código numérico fuera de todos los tramos conocidos (p.ej. 1011, 1015,
  // 2000-3999). Misma regla dura: si no sabemos qué es, se reporta.
  if (!range) {
    return { report: true, reason: 'unclassified' };
  }

  return { report: range.report, reason: range.reason };
}

export default classifyPusherClose;
